import { Component, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SecureFlowStep {
  id: number;
  title: string;
  body: string;
  nodes: string[];
  paths: string[];
  balls: string[];
  trust: boolean;
  chip?: string;
  attX?: number;
  attY?: number;
}

@Component({
  selector: 'app-secure-flow',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './secure-flow.component.html',
  styleUrl: './secure-flow.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SecureFlowComponent {
  currentIndex = 0;

  readonly steps: SecureFlowStep[] = [
    {
      id: 1,
      title: 'You sign in',
      body: 'You register on the website and reach the Browser — the tool that locks your model before it goes anywhere.',
      nodes: ['n-user', 'n-enc'],
      paths: ['p-user-enc'],
      balls: ['b-user-enc'],
      trust: false,
    },
    {
      id: 2,
      title: 'The vault proves it is real',
      body: 'Before anything moves, the TEE sends an attestation report so the Browser can confirm the vault is genuine and has not been tampered with.',
      nodes: ['n-tee-shell', 'n-buf', 'n-enc'],
      paths: ['p-tee-enc-attest'],
      balls: ['b-tee-enc-attest'],
      trust: true,
      chip: 'Checked and approved',
      attX: 492,
      attY: 148,
    },
    {
      id: 3,
      title: 'Your model goes in, locked',
      body: 'You encrypt your model so it cannot be read, then send it into the TEE — useless to anyone who intercepts it.',
      nodes: ['n-enc', 'n-tee-shell', 'n-buf'],
      paths: ['p-enc-buf'],
      balls: ['b-enc-buf'],
      trust: true,
    },
    {
      id: 4,
      title: 'Queued, then unlocked inside',
      body: 'Your model joins a queue. When its turn comes, it is decrypted — but only inside the sealed vault, where no one can watch.',
      nodes: ['n-tee-shell', 'n-buf', 'n-queue', 'n-dec'],
      paths: ['p-buf-q', 'p-buf-dec'],
      balls: ['b-buf-q', 'b-buf-dec'],
      trust: true,
    },
    {
      id: 5,
      title: 'Data owner verifies the vault',
      body: 'The TEE sends an attestation report to the Data Owner. Once the vault is verified, the Data Owner sends the encrypted test data into the TEE.',
      nodes: ['n-tee-shell', 'n-buf', 'n-data-owner'],
      paths: ['p-tee-owner-attest', 'p-owner-tee-data'],
      balls: ['b-tee-owner-attest', 'b-owner-tee-data'],
      trust: true,
      attX: 462,
      attY: 148,
    },
    {
      id: 6,
      title: 'Evaluation begins inside the vault',
      body: 'The same TEE becomes the private workspace for evaluation, keeping the model sealed while the evaluation environment is prepared.',
      nodes: ['n-tee-shell', 'n-buf', 'n-scr'],
      paths: ['p-proc-scr'],
      balls: ['b-proc-scr'],
      trust: true,
    },
    {
      id: 7,
      title: 'Sealed shut — nothing leaks out',
      body: 'The vault is locked down — it cannot send anything out, and that rule can be verified by anyone. Evaluation scripts and test data are brought in here.',
      nodes: ['n-tee-shell', 'n-buf', 'n-pol', 'n-scr'],
      paths: ['p-proc-pol', 'p-proc-scr'],
      balls: ['b-proc-pol', 'b-proc-scr'],
      trust: true,
      chip: 'Nothing can escape',
    },
    {
      id: 8,
      title: 'The test runs, out of sight',
      body: 'A keyholder unlocks the data, and your model is scored inside the sealed vault. No one can peek at your model or the data.',
      nodes: ['n-tee-shell', 'n-buf', 'n-eval'],
      paths: ['p-proc-eval'],
      balls: ['b-proc-eval'],
      trust: true,
      chip: 'Tested in private',
    },
    {
      id: 9,
      title: 'You see where you rank',
      body: 'The scores appear on the leaderboard, where you can sort and compare how your model did against the rest.',
      nodes: ['n-eval', 'n-lb'],
      paths: ['p-eval-lb'],
      balls: ['b-eval-lb'],
      trust: false,
    },
    {
      id: 10,
      title: 'Sensitive data is wiped',
      body: 'Upon job completion, the TEE is deallocated and clears model weights, decrypted data, keys, tokens, and temporary buffers.',
      nodes: ['n-tee-shell', 'n-buf', 'n-eval', 'n-clean'],
      paths: ['p-eval-clean'],
      balls: ['b-eval-clean'],
      trust: true,
      chip: 'Runtime cleared',
    },
  ];

  get current(): SecureFlowStep {
    return this.steps[this.currentIndex];
  }

  get stepNumber(): string {
    return String(this.current.id).padStart(2, '0');
  }

  get isLastStep(): boolean {
    return this.currentIndex === this.steps.length - 1;
  }

  isNodeActive(id: string): boolean {
    return this.current.nodes.includes(id);
  }

  isPathActive(id: string): boolean {
    return this.current.paths.includes(id);
  }

  isBallActive(id: string): boolean {
    return this.current.balls.includes(id);
  }

  isStepCurrent(index: number): boolean {
    return index === this.currentIndex;
  }

  isStepComplete(index: number): boolean {
    return index < this.currentIndex;
  }

  goToStep(index: number): void {
    this.currentIndex = index;
  }

  nextStep(): void {
    this.currentIndex = this.isLastStep ? 0 : this.currentIndex + 1;
  }
}
